import { Accordion } from "@/components/core-components";
import {
  Icons,
  LinkButton,
  Skeleton,
  Typography,
} from "@flash-tecnologia/hros-web-ui-v2";
import { CreateEmployeeError } from "../../hooks/use-create-members";
import { EmployeeErrorsAccordionHeader } from "./employee-errors-accordion-header";
import {
  ErrorMessage,
  List,
  Wrapper,
} from "./employee-errors-accordion.styles";
import { useGetPermissionsRoles } from "../../hooks/use-get-permissions-roles";
import { useMemo } from "react";
import { trpc } from "@/api/client";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";

type ErrorType = NonNullable<
  CreateEmployeeError["errors"][number]["operationType"]
>;

interface EmployeeErrorsAccordionProps {
  employees: CreateEmployeeError[];
}

export const EmployeeErrorsAccordion = ({
  employees,
}: EmployeeErrorsAccordionProps) => {
  const { selectedCompany } = useSelectedCompany();
  const { permissionRoles, isLoading: isGetPermissionRolesLoading } =
    useGetPermissionsRoles();
  const { data: departments, isLoading: isDepartmentsLoading } =
    trpc.getDepartments.useQuery(
      { companyId: selectedCompany.id },
      { enabled: !!selectedCompany.id }
    );
  const { data: jobRoles, isLoading: isGetJobRolesLoading } =
    trpc.getJobRoles.useQuery({
      companyId: selectedCompany.id,
    });

  const employeesWithErrors = useMemo(
    () =>
      employees.map((employee) => {
        const errors = employee.errors.map((err) => {
          let entity = err.operationId || "";

          switch (err.operationType) {
            case "department":
              const department = departments?.find(
                (department) => department.id === err.operationId
              );
              if (department) entity = department.name;
              break;
            case "jobRole":
              const jobRole = jobRoles?.find(
                (role) => role.id === err.operationId
              );
              if (jobRole) entity = jobRole.name;
              break;
            case "permissionRole":
              const role = permissionRoles?.find(
                (role) => role.id === err.operationId
              );
              if (role) entity = role.name;
              break;
            case "superAdmin":
              entity = "Super Administradores";
              break;
            default:
              break;
          }

          return { ...err, entity };
        });

        return {
          name: employee.employeeData.name,
          errors,
        };
      }),
    [employees, permissionRoles, jobRoles, departments]
  );

  if (
    isGetPermissionRolesLoading ||
    isGetJobRolesLoading ||
    isDepartmentsLoading
  ) {
    return <Skeleton height="300px" width="100%" />;
  }

  if (!employees.length) return null;

  return (
    <Accordion
      defaultExpanded
      customHeader={<EmployeeErrorsAccordionHeader />}
      maxHeight={230}
    >
      {employeesWithErrors.map((employee, idx) => (
        <Wrapper key={idx}>
          <Typography.Body4 weight={700} color="neutral.50">
            {employee.name}
          </Typography.Body4>
          <List>
            {employee.errors.map((err) => (
              <ErrorMessage key={`${err.operationType}-${err.operationId}`}>
                <Typography.Body4 color="neutral.50">
                  • Não foi possível adicionar em {err.entity}.
                </Typography.Body4>
                {err.operationType && <RedirectLink type={err.operationType} />}
              </ErrorMessage>
            ))}
          </List>
        </Wrapper>
      ))}
    </Accordion>
  );
};

const RedirectLink = ({ type }: { type: ErrorType }) => {
  const linkMap: Record<ErrorType, { text: string; href: string }> = {
    department: {
      text: "Editar departamentos",
      href: "/settings/departments",
    },
    jobRole: {
      text: "Editar cargo",
      href: "/settings/roles",
    },
    permissionRole: {
      text: "Editar perfil de permissão",
      href: "/settings/permissions/view",
    },
    group: {
      text: "Editar grupo",
      href: "/settings/groups/view",
    },
    superAdmin: {
      text: "Editar super administrador",
      href: "/settings/permissions/administrators/view",
    },
  };

  const link = linkMap[type];
  if (!link) return null;

  return (
    <LinkButton
      variant="default"
      underlined={false}
      href={link.href}
      target="_blank"
    >
      {link.text} <Icons name="IconExternalLink" size={16} />
    </LinkButton>
  );
};
